import React from 'react';
import { connect } from 'react-redux';
import { Card } from 'ui';
import Text, { TEXT_VARIANTS } from 'components/ui/Typography/Text';
import CalendarGraphic from 'components/InPersonTraining/shared/CalendarGraphic';
import AttendanceStatus from '../../shared/AttendanceStatus';
import SessionDetails from '../../shared/SessionDetails';

const EventsDashboardEvent = ({
  attendances,
  canChangeAttendance,
  event,
  session,
  siblingSessions,
  urls,
}) => {
  const isMultiSession = siblingSessions.length > 1;
  const eventRsvpType = !isMultiSession ? 'single_session' : event.rsvpType;
  const isSeries = eventRsvpType === 'all';
  const isSingle = eventRsvpType === 'single';
  const maxAttendees = !isSeries
    ? session.maxAttendees
    : siblingSessions.find(siblingSession => siblingSession.maxAttendees)
        ?.maxAttendees;

  const atCapacity = () => {
    if (isSeries) {
      return siblingSessions.some(siblingSession => {
        const { totalRegistered } = siblingSession;
        return maxAttendees && totalRegistered >= maxAttendees;
      });
    }
    return siblingSessions.every(siblingSession => {
      const { maxAttendees: sessionMaxAttendees, totalRegistered } =
        siblingSession;
      return sessionMaxAttendees && totalRegistered >= sessionMaxAttendees;
    });
  };

  const hasRsvp = !!attendances.length;
  const limitMet = atCapacity();

  const positiveStatus =
    hasRsvp &&
    attendances.find(sessionAttendance =>
      ['did_attend', 'will_attend'].includes(sessionAttendance.status),
    );

  const negativeStatus =
    hasRsvp &&
    !positiveStatus &&
    attendances.find(sessionAttendance =>
      ['did_not_attend', 'will_not_attend'].includes(sessionAttendance.status),
    );

  const hasPositiveStatus = !!positiveStatus;
  const willNotAttend = hasRsvp && !hasPositiveStatus;

  const eventInstructor = () => {
    if (!isMultiSession) {
      return session.trainer;
    }
    if (isSingle && hasPositiveStatus) {
      return {
        id: positiveStatus.trainer.id,
        name: positiveStatus.trainer.name,
      };
    }
    const hasOneInstructor = siblingSessions.every(siblingSession => {
      return siblingSession.trainer?.id === session.trainer?.id;
    });
    return hasOneInstructor
      ? session.trainer
      : { id: null, name: 'Multiple Instructors' };
  };

  const eventLocation = () => {
    if (!isMultiSession) {
      return session.location;
    }
    if (isSingle && hasPositiveStatus) {
      return positiveStatus.location;
    }
    const hasOneLocation = siblingSessions.every(siblingSession => {
      return siblingSession.location === session.location;
    });
    return hasOneLocation ? session.location : 'Multiple Locations';
  };

  const sortedSiblingSessions = siblingSessions.sort((a, b) =>
    moment(a.startTime).diff(moment(b.startTime)),
  );

  const hasVideoMeetings = () => {
    return siblingSessions.some(
      siblingSession => siblingSession.videoMeeting?.url,
    );
  };

  const videoMeetingUrl = () => {
    if (isSingle && hasPositiveStatus) {
      return positiveStatus.videoMeeting?.url;
    }
    return session.videoMeeting?.url;
  };

  const eventStartTime = () => {
    if (!isMultiSession) {
      return session.startTime;
    }
    if (isSingle && positiveStatus) {
      return positiveStatus.startTime;
    }
    return sortedSiblingSessions[0].startTime;
  };

  const eventEndTime = () => {
    if (!isMultiSession) {
      return session.endTime;
    }
    if (isSingle && positiveStatus) {
      return positiveStatus.endTime;
    }
    return sortedSiblingSessions.at(-1).endTime;
  };

  const nextStartTime = !isSeries
    ? eventStartTime()
    : sortedSiblingSessions.find(
        siblingSession => moment(siblingSession.endTime) >= moment(),
      )?.startTime || sortedSiblingSessions[0].startTime;

  const updatedSession = !isMultiSession
    ? { ...session }
    : {
        ...session,
        endTime: eventEndTime(),
        location: eventLocation(),
        maxAttendees,
        startTime: eventStartTime(),
        trainer: eventInstructor(),
        videoMeeting: {
          ...session.videoMeeting,
          url: videoMeetingUrl(),
        },
      };

  const nextOrCurrentSession = !isSeries
    ? { ...updatedSession }
    : { ...updatedSession, startTime: nextStartTime };

  const canRsvp = session.upcoming || canChangeAttendance;
  const showNoStatus = !hasRsvp && canRsvp && !limitMet;
  const showLimitMet = !hasRsvp && canRsvp && limitMet;
  const showDidNotAttend = !hasRsvp && !canRsvp;
  const canRegister =
    (canRsvp && !limitMet) || (canRsvp && limitMet && hasPositiveStatus);

  const videoMeetingText = () => {
    const hasMeetings = hasVideoMeetings();
    if (!hasMeetings) {
      return null;
    }
    if (isSeries) {
      return t(
        'in_person_training.event_overview.video_conference_link.view_details_to_join',
      );
    }
    if (isSingle && (willNotAttend || !hasRsvp)) {
      return t(
        'in_person_training.event_overview.video_conference_link.available',
      );
    }
    return null;
  };

  // Limit met info takes precedence over attendance status when cap is met
  const showLimitMetOverrideStatus = willNotAttend && limitMet;
  const showRsvpStatus = hasRsvp;
  const showSessionCount = isSeries || (isSingle && !hasPositiveStatus);
  const showAtCapacityChip = limitMet && (willNotAttend || !hasRsvp);

  return (
    <Card
      url={canRegister && urls.register}
      className="EventsDashboardEvent EventsCardShadow MyTrainingEventsCard ff-ss mb-24 d-b"
    >
      <CalendarGraphic convertToLocalTime session={nextOrCurrentSession} />
      <Card.Content padding="medium">
        <div className="mb-8">
          <Text className="text-wrap" variant={TEXT_VARIANTS.SUBHEADER_BOLD}>
            {event.name}
          </Text>
        </div>
        <SessionDetails
          className="mt-8"
          eventId={event.id}
          rsvpType={eventRsvpType}
          session={updatedSession}
          sessionCount={showSessionCount && siblingSessions.length}
          showRegistrationCap={!showAtCapacityChip}
          showVideoMeetingUrl={
            !isMultiSession || (isSingle && hasPositiveStatus)
          }
          videoMeetingText={videoMeetingText()}
        />
        {showRsvpStatus && (
          <div className="mt-8">
            <AttendanceStatus
              status={
                hasPositiveStatus
                  ? positiveStatus.status
                  : negativeStatus.status
              }
            />
          </div>
        )}
        {showLimitMetOverrideStatus && (
          <div className="mt-8">
            <AttendanceStatus status="limit_met" />
          </div>
        )}
        {showNoStatus && (
          <Text className="d-ib mt-8 h-td-u" variant={TEXT_VARIANTS.BODY_LINK}>
            {t('in_person_training.attendance.phrase.no_status')}
          </Text>
        )}
        {session.upcoming && showLimitMet && (
          <div className="mt-8">
            <AttendanceStatus status="limit_met" />
          </div>
        )}
        {showDidNotAttend && (
          <div className="mt-8">
            <AttendanceStatus status="did_not_attend" />
          </div>
        )}
        {!session.upcoming && showLimitMet && (
          <div className="mt-8">
            <AttendanceStatus status="did_not_attend" />
          </div>
        )}
      </Card.Content>
    </Card>
  );
};

const mapStateToProps = ({
  meta: {
    policies: { canChangeAttendance },
    features,
  },
}) => {
  return {
    canChangeAttendance,
  };
};

export default connect(mapStateToProps)(EventsDashboardEvent);
